import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Image,
  useToast,
  Heading,
  Text,
  Flex,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const Preferences = () => {
  const [clubName, setClubName] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [preferencesId, setPreferencesId] = useState(null);
  const toast = useToast();

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const { data, error } = await supabase
        .from('club_preferences')
        .select('*')
        .single();

      if (error) throw error;

      if (data) {
        setClubName(data.club_name);
        setLogoUrl(data.logo_url);
        setPreferencesId(data.id);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      toast({
        title: 'Error fetching preferences',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleLogoChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast({
            title: 'Invalid file type',
            description: 'Please upload an image file',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          return;
        }
        
        setLogoFile(file);
        // Create a preview URL for the uploaded image
        const previewUrl = URL.createObjectURL(file);
        setLogoUrl(previewUrl);
      } catch (error) {
        toast({
          title: 'Error',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const uploadLogo = async (file) => {
    try {
      console.log('Starting logo upload...', file);
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `club-logos/${fileName}`;

      console.log('Uploading to path:', filePath);
      const { error: uploadError, data } = await supabase.storage
        .from('logos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }

      console.log('Upload successful:', data);
      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(filePath);

      console.log('Public URL:', publicUrl);
      return publicUrl;
    } catch (error) {
      console.error('Error in uploadLogo:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log('Starting form submission...');
      let newLogoUrl = logoUrl;

      if (logoFile) {
        console.log('Uploading new logo file:', logoFile);
        // Delete old logo if it exists
        if (logoUrl && !logoUrl.startsWith('blob:')) {
          const oldPath = logoUrl.split('/').pop();
          console.log('Deleting old logo:', oldPath);
          await supabase.storage
            .from('logos')
            .remove([`club-logos/${oldPath}`]);
        }

        // Upload new logo
        newLogoUrl = await uploadLogo(logoFile);
      }

      console.log('Updating preferences with:', {
        club_name: clubName,
        logo_url: newLogoUrl,
      });

      // Update preferences
      const { error } = await supabase
        .from('club_preferences')
        .update({
          club_name: clubName,
          logo_url: newLogoUrl,
        })
        .eq('id', preferencesId);

      if (error) {
        console.error('Error updating preferences:', error);
        throw error;
      }

      toast({
        title: 'Preferences updated',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setLogoUrl(newLogoUrl);
      setLogoFile(null);
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      toast({
        title: 'Error updating preferences',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxW="container.md" mx="auto" py={8}>
      <Heading size="lg" mb={6}>Club Preferences</Heading>
      
      <form onSubmit={handleSubmit}>
        <VStack spacing={6} align="stretch">
          <FormControl>
            <FormLabel>Club Name</FormLabel>
            <Input
              value={clubName}
              onChange={(e) => setClubName(e.target.value)}
              placeholder="Enter club name"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Club Logo</FormLabel>
            <Text fontSize="sm" color="gray.600" mb={2}>
              Recommended size: 600x300 pixels (2:1 aspect ratio)
            </Text>
            
            {logoUrl && (
              <Box mb={4}>
                <Image
                  src={logoUrl}
                  alt="Club Logo Preview"
                  maxW="300px"
                  borderRadius="md"
                  boxShadow="sm"
                />
              </Box>
            )}
            
            <Input
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              sx={{
                '::file-selector-button': {
                  border: 'none',
                  outline: 'none',
                  mr: 2,
                  bg: 'blue.500',
                  color: 'white',
                  borderRadius: 'md',
                  px: 4,
                  py: 2,
                  cursor: 'pointer',
                  _hover: {
                    bg: 'blue.600'
                  }
                }
              }}
            />
          </FormControl>

          <Button
            type="submit"
            colorScheme="blue"
            isLoading={loading}
            loadingText="Saving..."
          >
            Save Preferences
          </Button>
        </VStack>
      </form>
    </Box>
  );
};

export default Preferences;
